import React, { useState, useEffect } from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Avatar, 
  CircularProgress, 
  Button, 
  Snackbar, 
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import CustomAppBar from '../components/appbar';
import { Buffer } from 'buffer';

const REACT_APP_API_USERNAME = process.env.REACT_APP_API_USERNAME;
const REACT_APP_API_PASSWORD = process.env.REACT_APP_API_PASSWORD;
const REACT_APP_EXPRESS_SERVER = process.env.REACT_APP_EXPRESS_SERVER;
const REACT_APP_COMPANY = process.env.REACT_APP_COMPANY;
const REACT_APP_BIDDING_API_DATA_URL = process.env.REACT_APP_BIDDING_API_DATA_URL;
const REACT_APP_API_DATA_URL = process.env.REACT_APP_API_DATA_URL;


const Contacts = () => {
    const [vendorData, setVendorData] = useState({
        vendorNo: '',
        vendorName: '',
        tin: '',
        email: '',
        phoneNo: '',
        currency: 'UGX',
        country: 'Uganda'
    });
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentContact, setCurrentContact] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const token = Buffer.from(`${REACT_APP_API_USERNAME}:${REACT_APP_API_PASSWORD}`, 'utf8').toString('base64');

    useEffect(() => {
        const fetchOptions = {
            method: 'GET',
            headers: {
            'Authorization': `Basic ${token}`,
            'Content-Type': 'application/json',
            },
        };
        const fetchVendorData = async () => {
            try {
                const storedEmail = sessionStorage.getItem('email');
                if (!storedEmail) {
                    throw new Error('No email found in local storage');
                }

                // Fetch vendor data
                const vendorResponse = await fetch(`${REACT_APP_EXPRESS_SERVER}/profile/${storedEmail}`);
                if (!vendorResponse.ok) {
                    // throw new Error('Failed to fetch vendor profile');
                }
                const vendorData = await vendorResponse.json();
                setVendorData(vendorData);

                // Fetch contacts
                const contactsResponse = await fetch(`${REACT_APP_API_DATA_URL}contacts?${REACT_APP_COMPANY}&$filter= (accType eq 'Vendor' and accNo eq '${vendorData.vendorNo}')`, fetchOptions);
                if (!contactsResponse.ok) {
                    throw new Error('Failed to fetch contacts');
                }
                const contactsData = await contactsResponse.json();
                setContacts(contactsData?.value);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchVendorData();
    }, []);

    const handleValidate = async () => {
        setValidationLoading(true);
        setValidationResult(null);
        
        try {
            const validationResponse = await fetch(`${REACT_APP_BIDDING_API_DATA_URL}createContactOrVendor?${REACT_APP_COMPANY}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${token}`
                },
                body: JSON.stringify({
                    name: vendorData.vendorName,
                    tin: vendorData.tin,
                    email: vendorData.email,
                    phoneNo: vendorData.phoneNo,
                    local: true,
                    vendorNo: vendorData.vendorNo || ""
                })
            });

            if (!validationResponse.ok) {
                throw new Error('Validation failed');
            }

            const result = await validationResponse.json();
            const newVendorNo = result.value;

            if (!newVendorNo) {
                throw new Error('No vendor number returned');
            }

            // Update vendor number in backend
            await fetch(`${REACT_APP_EXPRESS_SERVER}/users/${vendorData.username}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ vendorNo: newVendorNo })
            });

            setVendorData(prev => ({
                ...prev,
                vendorNo: newVendorNo
            }));

            sessionStorage.setItem('vendorNo', newVendorNo);

            setValidationResult({ 
                success: true, 
                message: `Validation successful! Vendor number: ${newVendorNo}` 
            });
        } catch (err) {
            setValidationResult({ 
                success: false, 
                message: err.message || 'Validation failed. Please try again.' 
            });
        } finally {
            setValidationLoading(false);
        }
    };

    const handleOpenDialog = (cont = null) => {
        setCurrentContact(cont || {
            name: '',
            phoneNo: '',
            email: '',
            address: '',
            jobTitle:'',
            accType: '',
            accNo: '',
        });
        setIsEditing(!!cont);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentContact(null);
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setCurrentContact(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveContact = async (currentContact) => {
        try {
            setLoading(true);
            const url = isEditing 
                ? `${REACT_APP_API_DATA_URL}contacts(${currentContact.SystemId})?${REACT_APP_COMPANY}`
                : `${REACT_APP_API_DATA_URL}contacts?${REACT_APP_COMPANY}`;
            
            const method = !!currentContact.SystemId ? 'PATCH' : 'POST';
            const custheaders =  {
                'Content-Type': 'application/json',
                'Authorization': `Basic ${token}`
            };
             if (!!currentContact.SystemId) {
                custheaders['If-Match'] = '*';
            }
            
            const response = await fetch(url, {
                method,
                headers:custheaders,
                body: JSON.stringify({
                    ...currentContact,
                    accType:'Vendor',
                    accNo: vendorData.vendorNo,
                })
                
            });

            if (!response.ok) {
                throw new Error(`Failed to ${isEditing ? 'update' : 'create'} contact`);
            }

            const updatedContact = await response.json();
            
            if (isEditing) {
                setContacts(prev => prev.map(c => 
                    c.SystemId === updatedContact.SystemId ? updatedContact : c
                ));
            } else {
                setContacts(prev => [...prev, updatedContact]);
            }

            handleCloseDialog();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteContact = async (id) => {
        try {
            setLoading(true);
            const response = await fetch(`${REACT_APP_API_DATA_URL}contacts(${id})?${REACT_APP_COMPANY}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete contact');
            }

            setContacts(prev => prev.filter(c => c.SystemId !== id));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setValidationResult(null);
    };

    if (loading && !contacts.length) {
        return (
            <Box>
                <CustomAppBar active="profile" />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <CustomAppBar active="profile" />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography color="error">{error}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <CustomAppBar active="profile" />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Vendor Information Section */}
                <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                        Vendor Information
                    </Typography>
                    
                    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={3}>
                        <Box>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Basic Information
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Vendor Number:</strong> {vendorData.vendorNo || 'Not assigned'}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Business Name:</strong> {vendorData.vendorName}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>TIN:</strong> {vendorData.tin}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Primary Contact
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Email:</strong> {vendorData.email}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Phone:</strong> {vendorData.phoneNo || 'Not provided'}
                            </Typography>
                        </Box>   

                        <Box>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Business Details
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Currency:</strong> {vendorData.currency}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                <strong>Country:</strong> {vendorData.country}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {vendorData.vendorNo ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CheckCircleIcon color="success" fontSize="large" />
                                    <Typography variant="body1" color="success.main">
                                        Validated
                                    </Typography>
                                </Box>
                            ) : (
                                <Button
                                    variant="contained"
                                    sx={{ backgroundColor: "green" }}
                                    onClick={handleValidate}
                                    disabled={validationLoading}
                                >
                                    {validationLoading ? <CircularProgress size={24} color="inherit" /> : 'Validate Vendor'}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Contacts Management Section */}
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Contact Persons
                        </Typography>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            Add Contact
                        </Button>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Contact Name</strong></TableCell>
                                    <TableCell><strong>Phone Number</strong></TableCell>
                                    <TableCell><strong>Email</strong></TableCell>
                                    <TableCell><strong>Job Title</strong></TableCell>
                                    <TableCell><strong>Address</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contacts.length > 0 ? (
                                    contacts.map((vendorcontact) => (
                                        <TableRow key={vendorcontact.SystemId}>
                                            <TableCell>{vendorcontact.name}</TableCell>
                                            <TableCell>{vendorcontact.phoneNo}</TableCell>
                                            <TableCell>{vendorcontact.email}</TableCell>
                                            <TableCell>{vendorcontact.jobTitle}</TableCell>
                                            <TableCell>{vendorcontact.address}</TableCell>
                                            <TableCell>
                                                <IconButton 
                                                    color="primary" 
                                                    onClick={() => handleOpenDialog(vendorcontact)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton 
                                                    color="error" 
                                                    onClick={() => handleDeleteContact(vendorcontact.SystemId)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No contacts found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>

            {/* Contact Edit/Create Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {isEditing ? 'Edit Contact' : 'Add New Contact'}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box component="form" sx={{ mt: 2 }}>
                        <TextField
                            label="Contact Name"
                            name="name"
                            value={currentContact?.name || ''}
                            onChange={handleContactChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            label="Phone Number"
                            name="phoneNo"
                            value={currentContact?.phoneNo || ''}
                            onChange={handleContactChange}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label="Email"
                            name="email"
                            value={currentContact?.email || ''}
                            onChange={handleContactChange}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label="Address"
                            name="address"
                            value={currentContact?.address || ''}
                            onChange={handleContactChange}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label="Job Title"
                            name="jobTitle"
                            value={currentContact?.jobTitle || ''}
                            onChange={handleContactChange}
                            fullWidth
                            margin="normal"
                        />
                        
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button 
                        onClick={()=>handleSaveContact(currentContact)} 
                        variant="contained" 
                        color="primary"
                        disabled={!currentContact?.name}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!validationResult}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={validationResult?.success ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {validationResult?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Contacts;